(() => {
  'use strict';

  const root = document.documentElement;
  const media = matchMedia('(max-width: 899px)');
  const hiddenClass = 'bp-wa-cta-obscured';
  const selectors = [
    '.bp-cart-page-summary .cart__checkout[name="checkout"]',
    '#CartDrawer.wt-cart__drawer--open .wt-cart__cta[name="checkout"]',
    'form[data-type="add-to-cart-form"] [name="add"][type="submit"]',
    'sticky-buy-button.wt-product__sticky-buy--show .wt-product__sticky-buy__button',
    '.bp-pdp .buyrow .cta',
    '.bp-pdp .dock.on .cta',
    '.bp-pdp #shCheckout',
    '.bp-pannello .buyrow .cta',
    '.bp-pannello .dock.on .cta',
    '.bp-pannello #shCheckout',
    '.bv-pdp .bar .cta',
    '.bv-panel .bar .cta',
    '.bp-bvs-ref__sticky.is-visible [data-sticky-cta]',
    '.bp-tinta-ref__sticky.is-visible [data-sticky-cta]'
  ].join(',');
  let widget = null;
  let widgetParent = null;
  let originalInert = false;
  let hidden = false;
  let frame = 0;
  let mobileActive = false;
  let mobileAbort = null;
  let unsubscribeCartUpdate = null;
  let discovery = null;
  let widgetParentObserver = null;
  let watchedTargets = new Set();

  const visibleRect = (element, rect) => {
    if (!rect.width || !rect.height || rect.right <= 0 || rect.bottom <= 0 ||
        rect.left >= innerWidth || rect.top >= innerHeight) return false;
    if (element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
    const style = getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
  };
  const intersects = (a, b) => a.left < b.right + 8 && a.right > b.left - 8 &&
    a.top < b.bottom + 8 && a.bottom > b.top - 8;
  const containsWidgetRoot = (node) => node.nodeType === 1 &&
    (node.id === 'wa-chat-btn-root' || Boolean(node.querySelector?.('#wa-chat-btn-root')));
  const addedWidgetRoot = (records) => records.some(record =>
    Array.from(record.addedNodes).some(containsWidgetRoot));

  const restore = () => {
    if (root.classList.contains(hiddenClass)) root.classList.remove(hiddenClass);
    if (hidden && widget) widget.inert = originalInert;
    hidden = false;
  };
  const hide = (target) => {
    if (hidden || !widget) return;
    if (widget.contains(document.activeElement)) {
      const focusTarget = target?.matches(':disabled') ? null : target;
      focusTarget?.focus({preventScroll: true});
    }
    originalInert = widget.inert;
    widget.inert = true;
    root.classList.add(hiddenClass);
    hidden = true;
  };

  const stateObserver = typeof MutationObserver === 'function' ? new MutationObserver(schedule) : null;
  const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;
  const bodyObserver = typeof MutationObserver === 'function' ? new MutationObserver(schedule) : null;

  function schedule() {
    if (!media.matches) {
      if (mobileActive) stopMobile();
      return;
    }
    if (!mobileActive || frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      measure();
    });
  }
  const discover = () => {
    if (discovery || widget || !mobileActive || !document.body || typeof MutationObserver !== 'function') return;
    discovery = new MutationObserver(records => {
      if (addedWidgetRoot(records)) schedule();
    });
    discovery.observe(document.body, {childList: true, subtree: true});
  };
  const observeWidgetAncestry = () => {
    widgetParentObserver?.disconnect();
    widgetParentObserver = null;
    widgetParent = widget?.parentElement ?? null;
    if (!widgetParent || typeof MutationObserver !== 'function') return;
    widgetParentObserver = new MutationObserver(records => {
      const movedAncestry = records.some(record => Array.from(record.removedNodes).some(node =>
        node === widget || Boolean(node.contains?.(widget))));
      if (movedAncestry) widgetParent = null;
      if (movedAncestry || addedWidgetRoot(records)) schedule();
    });
    for (let ancestor = widgetParent; ancestor; ancestor = ancestor.parentElement) {
      widgetParentObserver.observe(ancestor, {childList: true});
      if (ancestor === document.body) break;
    }
  };
  const attach = () => {
    const next = document.getElementById('wa-chat-btn-root');
    if (!next && !widget) { discover(); return; }
    if (next && next === widget && widgetParent === next.parentElement &&
        (widgetParentObserver || typeof MutationObserver !== 'function')) return;
    restore();
    if (widget) resizeObserver?.unobserve(widget);
    widgetParentObserver?.disconnect();
    widgetParentObserver = null;
    widget = next;
    originalInert = widget?.inert ?? false;
    if (widget) {
      discovery?.disconnect();
      discovery = null;
      resizeObserver?.observe(widget);
      observeWidgetAncestry();
    } else {
      widgetParent = null;
      discover();
    }
  };
  const updateTargetWatches = (next) => {
    if (next.size === watchedTargets.size &&
        Array.from(next).every(element => watchedTargets.has(element))) return;
    stateObserver?.disconnect();
    for (const element of watchedTargets) {
      if (!next.has(element)) resizeObserver?.unobserve(element);
    }
    for (const element of next) {
      if (!watchedTargets.has(element)) resizeObserver?.observe(element);
      stateObserver?.observe(element, {attributes: true, attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'open']});
    }
    watchedTargets = next;
  };
  const measure = () => {
    if (!mobileActive || !media.matches) return;
    attach();
    if (!widget) { updateTargetWatches(new Set()); return; }
    if (document.body.classList.contains('page-overlay-cart-on')) {
      updateTargetWatches(new Set());
      hide(document.querySelector('#CartDrawer .wt-cart__cta[name="checkout"]'));
      return;
    }
    const widgetRect = widget.getBoundingClientRect();
    const widgetStyle = getComputedStyle(widget);
    if (!widgetRect.width || !widgetRect.height || widgetStyle.display === 'none' ||
        (widgetStyle.visibility === 'hidden' && !hidden)) {
      updateTargetWatches(new Set());
      restore();
      return;
    }

    const targets = document.querySelectorAll(selectors);
    const nextWatched = new Set();
    for (const target of targets) {
      nextWatched.add(target);
      if (target.parentElement) nextWatched.add(target.parentElement);
      const owner = target.closest('.bp-pdp, .bp-pannello, .bv-pdp, .bv-panel, sticky-buy-button, .dock, .bar, .bp-bvs-ref__sticky, .bp-tinta-ref__sticky');
      if (owner) nextWatched.add(owner);
    }
    updateTargetWatches(nextWatched);
    let collision = null;
    for (const target of targets) {
      const rect = target.getBoundingClientRect();
      if (visibleRect(target, rect) && intersects(widgetRect, rect)) {
        collision = target;
        break;
      }
    }
    if (collision) hide(collision);
    else restore();
  };

  function startMobile() {
    if (mobileActive || !document.body) return;
    mobileActive = true;
    mobileAbort = new AbortController();
    const signal = mobileAbort.signal;
    bodyObserver?.observe(document.body, {attributes: true, attributeFilter: ['class']});
    window.addEventListener('scroll', schedule, {passive: true, signal});
    window.addEventListener('resize', schedule, {passive: true, signal});
    window.visualViewport?.addEventListener('resize', schedule, {passive: true, signal});
    window.visualViewport?.addEventListener('scroll', schedule, {passive: true, signal});
    document.addEventListener('click', schedule, {capture: true, signal});
    document.addEventListener('change', schedule, {capture: true, signal});
    document.addEventListener('transitionend', event => {
      if (event.target.closest?.(selectors)) schedule();
    }, {capture: true, signal});
    document.addEventListener('shopify:section:load', schedule, {signal});
    document.addEventListener('shopify:section:unload', schedule, {signal});
    document.addEventListener('cart:refresh', schedule, {signal});
    document.addEventListener('cart-drawer:refresh', schedule, {signal});
    document.addEventListener('visibilitychange', schedule, {signal});
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, schedule);
    }
    schedule();
  }
  function stopMobile() {
    if (!mobileActive) return;
    mobileActive = false;
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
    mobileAbort?.abort();
    mobileAbort = null;
    if (typeof unsubscribeCartUpdate === 'function') unsubscribeCartUpdate();
    unsubscribeCartUpdate = null;
    bodyObserver?.disconnect();
    discovery?.disconnect();
    discovery = null;
    widgetParentObserver?.disconnect();
    widgetParentObserver = null;
    widgetParent = null;
    stateObserver?.disconnect();
    resizeObserver?.disconnect();
    watchedTargets = new Set();
    restore();
  }
  const onMediaChange = () => {
    if (media.matches) startMobile();
    else stopMobile();
  };
  const start = () => {
    if (media.addEventListener) media.addEventListener('change', onMediaChange);
    else media.addListener?.(onMediaChange);
    document.fonts?.ready?.then(schedule).catch(() => {});
    onMediaChange();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();
})();
